import React, { useState, useEffect, useRef } from 'react';
import { List, SignOut, Gear, User, Info, Chat, PlayCircle } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import SettingsModal from './SettingsModal';
import AboutModal from './AboutModal';
import ContactModal from './ContactModal';
import LoadingOverlay from './LoadingOverlay';

// Define the worker URL (consistency)
const WORKER_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

const Header = ({ title, onStartTour }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState(null); // State to hold user email
  const [isLoadingUser, setIsLoadingUser] = useState(true); // Loading state for user data
  const menuRef = useRef(null);

  // Fetch user data when component mounts
  useEffect(() => {
    const fetchUserEmail = async () => {
      setIsLoadingUser(true);
      const token = localStorage.getItem('firebaseIdToken');

      if (!token) {
        console.log("Header: No token found, user not logged in.");
        setUserEmail(null);
        setIsLoadingUser(false);
        return;
      }

      try {
        const response = await fetch(`${WORKER_URL}/api/auth/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUserEmail(data.email); // Assuming the endpoint returns { email: '...' }
        } else if (response.status === 401) {
            console.error("Header: Token invalid or expired.");
            localStorage.removeItem('firebaseIdToken'); // Clear invalid token
            setUserEmail(null);
        } else {
            console.error("Header: Failed to fetch user data:", response.status);
            setUserEmail(null);
        }
      } catch (error) {
        console.error("Header: Error fetching user data:", error);
        setUserEmail(null);
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchUserEmail();
  }, []);

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = async () => {
    try {
      // Clear local storage
      localStorage.removeItem('firebaseIdToken');
      localStorage.removeItem('userProfile');
      
      // Navigate to login page
      navigate('/login');
      setIsMenuOpen(false);
    } catch (error) {
      // This catch block might be less relevant now unless other async ops were added
      console.error("Error during logout process: ", error);
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setIsMenuOpen(false);
  };

  const closeSettingsModal = () => {
    setIsSettingsModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        const menuButton = document.getElementById('menu-button');
        if (!menuButton || !menuButton.contains(event.target)) {
           setIsMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  return (
    <>
      <LoadingOverlay 
        isLoading={isLoadingUser} 
        message="در حال بارگذاری اطلاعات کاربر..."
      />
      <header className="app-header">
        <div className="flex justify-between items-center">
          <div className="text-xl font-extrabold text-purple-600">
            درمووایز
          </div>
          {title && (
            <div className="absolute left-1/2 transform -translate-x-1/2 text-lg font-bold text-gray-800">
               {title}
            </div>
          )}
          <div className="relative" ref={menuRef}>
            <button 
              id="menu-button" 
              onClick={handleMenuClick} 
              className="p-2 z-30 hover:bg-purple-50 rounded-lg transition-colors"
            >
              <List size={24} className="text-purple-600" />
            </button>

            {isMenuOpen && (
              <div className="absolute top-full left-0 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                    {isLoadingUser ? 'در حال بارگذاری...' : (userEmail || 'کاربر مهمان')}
                </div>
                
                {/* --- Navigation Menu Items --- */}
                <button
                  onClick={() => { navigate('/profile'); setIsMenuOpen(false); }}
                  className="w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 rtl:space-x-reverse"
                >
                   <User size={16} />
                  <span>پروفایل</span>
                </button>
                <button
                  onClick={() => { setIsAboutModalOpen(true); setIsMenuOpen(false); }}
                  className="w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 rtl:space-x-reverse"
                >
                  <Info size={16} />
                  <span>درباره ما</span>
                </button>
                <button
                  onClick={() => { setIsContactModalOpen(true); setIsMenuOpen(false); }}
                  className="w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 rtl:space-x-reverse"
                >
                   <Chat size={16} />
                  <span>تماس با ما</span>
                </button>

                {/* Tour Button */}
                <button
                  onClick={() => { 
                    onStartTour && onStartTour(); 
                    setIsMenuOpen(false); 
                  }}
                  className="w-full text-right px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center space-x-2 rtl:space-x-reverse"
                >
                  <PlayCircle size={16} />
                  <span>تور آموزشی</span>
                </button>

                {/* Existing Items - maybe add a divider? */}
                <div className="my-1 h-px bg-gray-100"></div>

                <button
                  onClick={handleSettingsClick}
                  className="w-full text-right px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 rtl:space-x-reverse"
                >
                  <Gear size={16} />
                  <span>تنظیمات</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-right px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center space-x-2 rtl:space-x-reverse"
                >
                  <SignOut size={16} />
                  <span>خروج</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <SettingsModal 
        isOpen={isSettingsModalOpen} 
        onClose={closeSettingsModal} 
        onStartTour={onStartTour}
      />
      <AboutModal isOpen={isAboutModalOpen} onClose={() => setIsAboutModalOpen(false)} />
      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </>
  );
};

export default Header;